import {IResource} from '../../../../models/resource';
import {ICreateCleanUpForm} from '../index';
import s from '../index.module.scss';

interface Props {
    form: ICreateCleanUpForm;
    litterBags: IResource[];
    generalResources: IResource[];
    onChange: (updates: Partial<ICreateCleanUpForm>) => void;
}

export default function Step4Resources({form, litterBags, generalResources, onChange}: Props) {
    const toggleLitterBag = (resourceId: string) => {
        const exists = form.litter_bags.find((lb) => lb.resource_id === resourceId);
        if (exists) {
            onChange({litter_bags: form.litter_bags.filter((lb) => lb.resource_id !== resourceId)});
        } else {
            onChange({
                litter_bags: [...form.litter_bags, {resource_id: resourceId, full: '', half: ''}],
            });
        }
    };

    const updateLitterBag = (resourceId: string, field: 'full' | 'half', value: string) =>
        onChange({
            litter_bags: form.litter_bags.map((lb) =>
                lb.resource_id === resourceId ? {...lb, [field]: value} : lb
            ),
        });

    const toggleResource = (resourceId: string) => {
        const exists = form.general_resources.find((gr) => gr.resource_id === resourceId);
        if (exists) {
            onChange({
                general_resources: form.general_resources.filter(
                    (gr) => gr.resource_id !== resourceId
                ),
            });
        } else {
            onChange({
                general_resources: [
                    ...form.general_resources,
                    {resource_id: resourceId, amount: ''},
                ],
            });
        }
    };

    const updateResource = (resourceId: string, value: string) =>
        onChange({
            general_resources: form.general_resources.map((gr) =>
                gr.resource_id === resourceId ? {...gr, amount: value} : gr
            ),
        });

    const hasContent = litterBags.length > 0 || generalResources.length > 0;

    return (
        <div className={s.stepBody}>
            {!hasContent && (
                <p className={s.emptyState}>No resources configured for this program.</p>
            )}

            {litterBags.length > 0 && (
                <>
                    <div className={s.subSectionTitle}>Litter Bags Used</div>
                    {litterBags.map((lb) => {
                        const entry = form.litter_bags.find((e) => e.resource_id === lb.id);
                        return (
                            <div key={lb.id} className={s.resourceRow}>
                                <label className={s.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={!!entry}
                                        onChange={() => toggleLitterBag(lb.id)}
                                    />
                                    {lb.title}
                                    {lb.capacity ? ` (${lb.capacity})` : ''}
                                </label>
                                {entry && (
                                    <div className={s.resourceInputs}>
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder="Full"
                                            value={entry.full}
                                            onChange={(e) =>
                                                updateLitterBag(lb.id, 'full', e.target.value)
                                            }
                                            className={s.smallInput}
                                        />
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder="Half"
                                            value={entry.half}
                                            onChange={(e) =>
                                                updateLitterBag(lb.id, 'half', e.target.value)
                                            }
                                            className={s.smallInput}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </>
            )}

            {generalResources.length > 0 && (
                <>
                    <div className={s.subSectionTitle}>General Resources Used</div>
                    {generalResources.map((gr) => {
                        const entry = form.general_resources.find(
                            (e) => e.resource_id === gr.id
                        );
                        return (
                            <div key={gr.id} className={s.resourceRow}>
                                <label className={s.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={!!entry}
                                        onChange={() => toggleResource(gr.id)}
                                    />
                                    {gr.title}
                                </label>
                                {entry && (
                                    <input
                                        type="number"
                                        min="0"
                                        placeholder="Amount"
                                        value={entry.amount}
                                        onChange={(e) =>
                                            updateResource(gr.id, e.target.value)
                                        }
                                        className={s.smallInput}
                                    />
                                )}
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
}
